import { nextTick } from 'vue'

const editors = '.va-form, [data-session-form]'
const selection = '.va-select__anchor .va-input-wrapper__field'
const controls = `input:not([type=button]):not([type=submit]), textarea, select, [contenteditable=true], ${selection}`

export function readFormValues(form) {
  return JSON.stringify(Array.from(form.querySelectorAll(controls)).map(field => {
    if (field.matches(selection)) {
      const copy = field.cloneNode(true)
      copy.querySelectorAll('.va-icon, button, input').forEach(node => node.remove())
      return ['selection', copy.textContent.replace(/\s+/g, ' ').trim()]
    }
    if (field.type === 'checkbox' || field.type === 'radio') return [field.type, field.name, field.value, field.checked]
    if (field.type === 'file') return ['file', Array.from(field.files || []).map(file => [file.name, file.size, file.lastModified])]
    if (field.tagName === 'SELECT') return ['select', Array.from(field.selectedOptions).map(option => option.value)]
    return [field.tagName, field.name, field.isContentEditable ? field.innerHTML : field.value]
  }))
}

// Snapshots live only in this tab's memory. No drafts or medical data are persisted.
export function createUnsavedForms(doc = document, view = window, snapshot = readFormValues) {
  const records = new Map()
  let disposed = false
  const visible = form => form?.isConnected && form.getClientRects().length > 0 &&
    !['hidden', 'collapse'].includes(view.getComputedStyle(form).visibility)
  const scan = () => {
    const forms = Array.from(doc.querySelectorAll(editors)).filter(visible)
    for (const form of records.keys()) if (!forms.includes(form)) records.delete(form)
    for (const form of forms) {
      const record = records.get(form)
      if (!record || !record.armed) records.set(form, { baseline: snapshot(form), armed: false })
    }
    return forms
  }
  const arm = event => {
    // Freeze before the first interaction, after async API/default values have rendered.
    const form = event.target.closest?.(editors)
    if (!form || !visible(form)) return
    let record = records.get(form)
    if (!record) { record = { baseline: snapshot(form), armed: false }; records.set(form, record) }
    if (!record.armed) {
      if (!['input', 'change'].includes(event.type)) record.baseline = snapshot(form)
      record.armed = true
    }
  }
  const events = ['pointerdown', 'focusin', 'keydown', 'beforeinput', 'input', 'change']
  events.forEach(name => doc.addEventListener(name, arm, true))
  const observer = new view.MutationObserver(scan)
  observer.observe(doc.documentElement, { subtree: true, childList: true, attributes: true })
  scan()
  return {
    hasChanges() {
      scan()
      return Array.from(records).some(([form, record]) => record.armed &&
        form.getAttribute('data-session-ignore') !== 'true' && snapshot(form) !== record.baseline)
    },
    reset() { records.clear(); scan() },
    // Use after an in-place save that keeps the editor visible.
    async saved(form) {
      await nextTick()
      if (!disposed && visible(form)) records.set(form, { baseline: snapshot(form), armed: false })
    },
    dispose() {
      disposed = true
      observer.disconnect()
      events.forEach(name => doc.removeEventListener(name, arm, true))
      records.clear()
    },
  }
}
